import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CUSTOMER_TAX_CONDITIONS,
  DOC_TYPES,
  PAYMENT_METHODS,
  PAYMENT_METHOD_KEYS,
  balanceLabel,
  normalizeDoc,
  normalizePhone,
  uuidv7,
  validateCustomerName,
  type CustomerTaxCondition,
  type DocType,
  type PaymentMethod,
} from '@pos/core';
import { applyLocalChanges, type CustomerRow } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { requestSync } from '../../services/sync.ts';
import { Alert, Button, Field, Input, Select, cx, errorMessage } from '../../ui/index.tsx';
import { Dialog } from '../../ui/Dialog.tsx';
import { MoneyInput } from '../../ui/MoneyInput.tsx';
import { useMoney } from '../products/data.ts';
import { useOpenSession } from '../cash/data.ts';
import { usePosSettings } from '../pos/state.ts';
import { filterCustomers, useBalances, useCustomers } from './data.ts';

// ---------------------------------------------------------------------------
// Alta y edición
// ---------------------------------------------------------------------------

interface Form {
  name: string;
  phone: string;
  email: string;
  doc_type: DocType | '';
  doc_number: string;
  tax_condition: CustomerTaxCondition;
  address: string;
  credit_limit: number | null;
  notes: string;
}

const emptyForm: Form = { name: '', phone: '', email: '', doc_type: '', doc_number: '', tax_condition: 'consumidor_final', address: '', credit_limit: null, notes: '' };

const fromRow = (c: CustomerRow): Form => ({
  name: c.name,
  phone: c.phone ?? '',
  email: c.email ?? '',
  doc_type: c.doc_type ?? '',
  doc_number: c.doc_number ?? '',
  tax_condition: c.tax_condition,
  address: c.address ?? '',
  credit_limit: c.credit_limit,
  notes: c.notes ?? '',
});

const orNull = (s: string) => (s.trim() ? s.trim() : null);

/** Alta (customer = null) o edición de un cliente. onSaved recibe el id. */
export function CustomerFormDialog({
  open,
  customer,
  initialName,
  onClose,
  onSaved,
}: {
  open: boolean;
  customer: CustomerRow | null;
  initialName?: string;
  onClose: () => void;
  onSaved?: (id: string) => void;
}) {
  const { business, writer } = useSession();
  const customers = useCustomers();
  const [form, setForm] = useState<Form>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof Form, string>>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(customer ? fromRow(customer) : { ...emptyForm, name: initialName ?? '' });
    setErrors({});
    setError(null);
  }, [open, customer, initialName]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const next: typeof errors = {};
    const nameError = validateCustomerName(form.name);
    if (nameError) next.name = nameError;
    let doc: string | null = null;
    if (form.doc_type) {
      doc = normalizeDoc(form.doc_type, form.doc_number);
      if (!doc) next.doc_number = form.doc_type === 'DNI' ? 'DNI de 7 u 8 números.' : `${form.doc_type} de 11 números.`;
      else if (customers?.some((c) => c.id !== customer?.id && c.doc_type === form.doc_type && c.doc_number === doc)) next.doc_number = 'Ya hay un cliente con ese documento.';
    }
    setErrors(next);
    if (Object.keys(next).length) return;

    const values = {
      name: form.name.trim(),
      phone: orNull(normalizePhone(form.phone)),
      email: orNull(form.email),
      doc_type: form.doc_type || null,
      doc_number: form.doc_type ? doc : null,
      tax_condition: form.tax_condition,
      address: orNull(form.address),
      credit_limit: form.credit_limit,
      notes: orNull(form.notes),
    };
    setBusy(true);
    setError(null);
    try {
      const now = new Date().toISOString();
      let id = customer?.id;
      if (customer) {
        const changes = Object.fromEntries(Object.entries(values).filter(([k, v]) => customer[k as keyof CustomerRow] !== v));
        if (Object.keys(changes).length) await applyLocalChanges(db, [{ table: 'customers', kind: 'patch', id: customer.id, changes }], writer);
      } else {
        id = uuidv7();
        await applyLocalChanges(
          db,
          [{ table: 'customers', kind: 'insert', row: { id, business_id: business.id, ...values, active: true, deleted_at: null, created_by: writer.user_id, created_at: now, updated_at: now } }],
          writer,
        );
      }
      requestSync(0);
      onSaved?.(id!);
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={customer ? 'Editar cliente' : 'Nuevo cliente'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={submit} loading={busy}>
            Guardar
          </Button>
        </>
      }
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label="Nombre" error={errors.name}>
          {(id) => <Input id={id} autoFocus value={form.name} maxLength={80} onChange={(e) => set('name', e.target.value)} />}
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Teléfono">{(id) => <Input id={id} type="tel" inputMode="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} />}</Field>
          <Field label="Email">{(id) => <Input id={id} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />}</Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-[8rem_1fr]">
          <Field label="Documento">
            {(id) => (
              <Select id={id} value={form.doc_type} onChange={(e) => set('doc_type', e.target.value as DocType | '')}>
                <option value="">Sin documento</option>
                {DOC_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Número" error={errors.doc_number}>
            {(id) => <Input id={id} inputMode="numeric" disabled={!form.doc_type} value={form.doc_number} invalid={!!errors.doc_number} onChange={(e) => set('doc_number', e.target.value)} />}
          </Field>
        </div>
        <Field label="Condición frente al IVA" hint="Para facturar más adelante (ARCA).">
          {(id) => (
            <Select id={id} value={form.tax_condition} onChange={(e) => set('tax_condition', e.target.value as CustomerTaxCondition)}>
              {Object.entries(CUSTOMER_TAX_CONDITIONS).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Dirección">{(id) => <Input id={id} value={form.address} maxLength={200} onChange={(e) => set('address', e.target.value)} />}</Field>
        <Field label="Límite de cuenta corriente" hint="Vacío = sin límite. Un empleado no puede vender a cuenta por encima del límite.">
          {(id) => <MoneyInput id={id} value={form.credit_limit} onChange={(c) => set('credit_limit', c)} />}
        </Field>
        <Field label="Notas">{(id) => <Input id={id} value={form.notes} maxLength={500} onChange={(e) => set('notes', e.target.value)} />}</Field>
        {error && <Alert tone="error">{error}</Alert>}
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Pago de cuenta corriente / ajuste (solo dueño)
// ---------------------------------------------------------------------------

export function AccountEntryDialog({
  customer,
  mode,
  balance,
  onClose,
}: {
  customer: CustomerRow;
  mode: 'payment' | 'adjustment' | null;
  balance: number;
  onClose: () => void;
}) {
  const { business, device, writer } = useSession();
  const money = useMoney();
  const settings = usePosSettings();
  const session = useOpenSession();
  const [amount, setAmount] = useState<number | null>(null);
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [direction, setDirection] = useState<'up' | 'down'>('down');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setAmount(mode === 'payment' && balance > 0 ? balance : null);
    setMethod('cash');
    setDirection('down');
    setNote('');
    setError(null);
  }, [mode, balance]);

  if (!mode) return <Dialog open={false} onClose={onClose} title="" children={null} />;
  const methods = PAYMENT_METHOD_KEYS.filter((m) => m !== 'account' && (settings.payments[m].enabled || m === 'cash'));

  const submit = async () => {
    if (!amount || amount <= 0) return setError('Escribí el importe.');
    if (mode === 'adjustment' && note.trim().length < 2) return setError('Escribí el motivo del ajuste.');
    const cashSession = mode === 'payment' && method === 'cash' ? (session?.id ?? null) : null;
    if (mode === 'payment' && method === 'cash' && !cashSession && settings.require_open_cash) {
      return setError('Para cobrar en efectivo abrí la caja (así el cobro queda en el arqueo).');
    }
    setBusy(true);
    setError(null);
    try {
      const now = new Date().toISOString();
      const signed = mode === 'payment' ? -amount : direction === 'down' ? -amount : amount;
      await applyLocalChanges(
        db,
        [
          {
            table: 'account_entries',
            kind: 'insert',
            row: {
              id: uuidv7(),
              business_id: business.id,
              customer_id: customer.id,
              type: mode,
              amount: signed,
              method: mode === 'payment' ? method : null,
              sale_id: null,
              cash_session_id: cashSession,
              note: note.trim() || null,
              occurred_at: now,
              device_id: device.id,
              created_by: writer.user_id,
              created_at: now,
            },
          },
        ],
        writer,
      );
      requestSync(0);
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={mode === 'payment' ? `Pago de ${customer.name}` : `Ajuste de cuenta de ${customer.name}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={submit} loading={busy}>
            Guardar
          </Button>
        </>
      }
    >
      <form
        className="space-y-3 text-sm"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <p className="text-slate-600">Saldo actual: {balanceLabel(balance, money)}</p>
        {mode === 'adjustment' && (
          <Field label="Tipo de ajuste">
            {(id) => (
              <Select id={id} value={direction} onChange={(e) => setDirection(e.target.value as 'up' | 'down')}>
                <option value="down">Baja la deuda (ej.: bonificación, error a favor del cliente)</option>
                <option value="up">Sube la deuda (ej.: saldo anterior en el cuaderno)</option>
              </Select>
            )}
          </Field>
        )}
        <Field label="Importe">{(id) => <MoneyInput id={id} autoFocus value={amount} onChange={setAmount} />}</Field>
        {mode === 'payment' && (
          <Field label="Cómo paga">
            {(id) => (
              <Select id={id} value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
                {methods.map((m) => (
                  <option key={m} value={m}>
                    {PAYMENT_METHODS[m].label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <Field label={mode === 'adjustment' ? 'Motivo' : 'Nota (opcional)'}>
          {(id) => <Input id={id} value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />}
        </Field>
        {amount ? (
          <p className="font-medium">
            Saldo después: {balanceLabel(balance + (mode === 'payment' || direction === 'down' ? -amount : amount), money)}
          </p>
        ) : null}
        {error && <Alert tone="error">{error}</Alert>}
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Elegir cliente (punto de venta)
// ---------------------------------------------------------------------------

export function CustomerPickerDialog({ open, onClose, onPick }: { open: boolean; onClose: () => void; onPick: (customer: CustomerRow) => void }) {
  const { can } = useSession();
  const customers = useCustomers();
  const balances = useBalances();
  const money = useMoney();
  const [query, setQuery] = useState('');
  // Teclado: las flechas marcan un cliente y Enter lo elige. Sin nada escrito no hay ninguno
  // marcado, para que un Enter de más no elija al primero de la lista.
  const [highlight, setHighlight] = useState(-1);
  const list = useRef<HTMLUListElement>(null);
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setHighlight(-1);
  }, [open]);
  const results = useMemo(() => filterCustomers((customers ?? []).filter((c) => c.active), query).slice(0, 50), [customers, query]);
  const showBalance = can('customer_accounts');

  useEffect(() => {
    if (highlight >= 0) list.current?.children[highlight]?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  return (
    <>
      <Dialog
        open={open && !creating}
        onClose={onClose}
        title="Elegir cliente"
        footer={
          <>
            {can('manage_customers') && (
              <Button variant="secondary" onClick={() => setCreating(true)}>
                Nuevo cliente
              </Button>
            )}
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            type="search"
            autoFocus
            placeholder="Nombre, teléfono o documento…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlight(e.target.value.trim() ? 0 : -1);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlight((h) => Math.max(0, Math.min(results.length - 1, h + (e.key === 'ArrowDown' ? 1 : -1))));
              } else if (e.key === 'Enter' && results[highlight]) {
                e.preventDefault();
                onPick(results[highlight]!);
              }
            }}
            aria-label="Buscar cliente"
          />
          <ul ref={list} className="max-h-80 divide-y divide-slate-100 overflow-y-auto rounded-lg ring-1 ring-slate-200">
            {results.length === 0 && <li className="p-4 text-center text-sm text-slate-500">{customers?.length ? 'Ningún cliente coincide.' : 'Todavía no hay clientes.'}</li>}
            {results.map((c, i) => {
              const b = balances?.get(c.id) ?? 0;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    className={cx('flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm', i === highlight ? 'bg-slate-100' : 'hover:bg-slate-50')}
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => onPick(c)}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{c.name}</span>
                      <span className="block truncate text-xs text-slate-500">{[c.phone, c.doc_number].filter(Boolean).join(' · ')}</span>
                    </span>
                    {showBalance && <span className={cx('shrink-0 text-xs', b > 0 ? 'text-amber-700' : 'text-slate-500')}>{balanceLabel(b, money)}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </Dialog>
      <CustomerFormDialog
        open={open && creating}
        customer={null}
        initialName={query}
        onClose={() => setCreating(false)}
        onSaved={async (id) => {
          setCreating(false);
          const c = await db.customers.get(id);
          if (c) onPick(c);
        }}
      />
    </>
  );
}
